import { lazy, Suspense } from 'react'
import { createBrowserRouter, Navigate, Outlet, type RouteObject } from 'react-router-dom'
import { DotLoader } from 'react-spinners'

// ログイン関連（即座に読み込み）
import AuthLogin from '@/views/auth/login'
import FirstLogin from '@/views/auth/first-login'
import ResetPassword from '@/views/auth/reset-password'

// エラー画面（lazy loading）
const Error400 = lazy(() => import('@/views/error/400'))
const Error401 = lazy(() => import('@/views/error/401'))
const Error403 = lazy(() => import('@/views/error/403'))
const Error404 = lazy(() => import('@/views/error/404'))
const Error408 = lazy(() => import('@/views/error/408'))
const Error500 = lazy(() => import('@/views/error/500'))

// レイアウト（即座に読み込み）
import MainLayout from '@/layouts/MainLayout'

// ダッシュボード（lazy loading）
const Dashboard = lazy(() => import('@/views/dashboards'))

// 登録情報（lazy loading）
const Contract = lazy(() => import('@/views/contract'))

// お問い合わせ（lazy loading）
const Contact = lazy(() => import('@/views/contact/contact-list'))
const ContactDetail = lazy(() => import('@/views/contact/contact-detail'))
const ContactAdd = lazy(() => import('@/views/contact/contact-add'))

// 案件（lazy loading）
const Projects = lazy(() => import('@/views/projects/project-list'))
const ProjectDetail = lazy(() => import('@/views/projects/project-detail'))
const ProjectAdd = lazy(() => import('@/views/projects/project-add'))
const FaqPage = lazy(() => import('@/views/help/faq'))
const ManualPage = lazy(() => import('@/views/help/manual'))

// 認証関連ルート
export const authRoutes: RouteObject[] = [
  {
    path: '/login',
    element: <AuthLogin />,
  },
  {
    path: '/first-login',
    element: <FirstLogin />,
  },
  {
    path: '/reset-password',
    element: <ResetPassword />,
  },
]

// エラーページルート
export const errorRoutes: RouteObject[] = [
  { path: '/error/400', element: <Error400 /> },
  { path: '/error/401', element: <Error401 /> },
  { path: '/error/403', element: <Error403 /> },
  { path: '/error/404', element: <Error404 /> },
  { path: '/error/408', element: <Error408 /> },
  { path: '/error/500', element: <Error500 /> },
]

// ダッシュボードルート
export const dashboardRoutes: RouteObject[] = [
  {
    path: '/dashboards',
    element: <Dashboard />,
  },
]

// 登録情報ルート
export const contractRoutes: RouteObject[] = [
  {
    path: '/contract',
    element: <Contract />,
  },
]

// お問い合わせルート
export const contactRoutes: RouteObject[] = [
  {
    path: '/contact',
    element: <Contact />,
  },
  {
    path: '/contact/detail/:id',
    element: <ContactDetail />,
  },
  {
    path: '/contact/add',
    element: <ContactAdd />,
  },
]

// 案件ルート
export const projectRoutes: RouteObject[] = [
  {
    path: '/projects',
    element: <Projects />,
  },
  {
    path: '/projects/detail/:id',
    element: <ProjectDetail />,
  },
  {
    path: '/projects/add',
    element: <ProjectAdd />,
  },
]

export const helpRoutes: RouteObject[] = [
  {
    path: '/help/faq',
    element: <FaqPage />,
  },
  {
    path: '/help/manual',
    element: <ManualPage />,
  },
]

// ログイン前のルート
export const notSignInRoutes: RouteObject[] = [
  {
    path: '/',
    element: <Navigate to="/login" replace />,
  },
  ...authRoutes,
  ...errorRoutes,
]

// サーバーエラー表示用フォールバック（ルート errorElement 用）
const Error500Fallback = () => (
  <div className="loading-overlay">
    <DotLoader color="var(--irogami-primary)" size={40} speedMultiplier={1} />
  </div>
)

// ログイン後のルート（メニューありレイアウトでラップ）
export const signInRoutes: RouteObject[] = [
  {
    element: <MainLayout />,
    errorElement: (
      <Suspense fallback={<Error500Fallback />}>
        <Error500 />
      </Suspense>
    ),
    children: [
      {
        path: '/',
        element: <Navigate to="/dashboards" replace />,
      },
      ...dashboardRoutes,
      ...contractRoutes,
      ...contactRoutes,
      ...projectRoutes,
      ...helpRoutes,
    ],
  },
]

// 全体ルート（ルート階層で errorElement を指定し、レンダー時のエラーで 500 を表示）
export const routes: RouteObject[] = [
  {
    element: <Outlet />,
    errorElement: (
      <Suspense fallback={<Error500Fallback />}>
        <Error500 />
      </Suspense>
    ),
    children: [
      ...notSignInRoutes,
      ...signInRoutes,
      // 存在しないパスにアクセスした場合は 404
      {
        path: '*',
        element: <Error404 />,
      },
    ],
  },
]

export const router = createBrowserRouter(routes, {
  future: {
    v7_startTransition: true,
  } as any,
})

